import React, { useState, useEffect, useMemo } from 'react';
import LZString from 'lz-string';
import './salesAnalytics.css';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const SalesAnalytics = ({ inventory, variantSales, coreProducts }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(50);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [productCosts, setProductCosts] = useState({});
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Get all unique variant titles from the inventory
    const allVariants = useMemo(() => {
        const variantSet = new Set();
        inventory.forEach(product => {
            product.variants.forEach(variant => {
                variantSet.add(variant.title);
            });
        });
        return Array.from(variantSet).sort();
    }, [inventory]);

    // Get all unique categories from product tags
    const allCategories = useMemo(() => {
        const categorySet = new Set();
        inventory.forEach(product => {
            if (Array.isArray(product.tags)) {
                product.tags.forEach(tag => {
                    if (tag.startsWith('stock-')) {
                        categorySet.add(tag);
                    }
                });
            }
        });
        return Array.from(categorySet).sort();
    }, [inventory]);

    // Process inventory data to get unique products
    const processedData = useMemo(() => {
        if (!inventory || inventory.length === 0) return [];

        // Get unique products
        const uniqueProducts = Array.from(
            new Map(inventory.map(item => [item.product_id, item])).values()
        );

        return uniqueProducts;
    }, [inventory]);

    // Memoize the decompressed orders data to avoid repeated decompression
    const decompressedOrdersData = useMemo(() => {
        const compressedData = sessionStorage.getItem('rawOrdersData');
        if (!compressedData) {
            return [];
        }

        try {
            return JSON.parse(LZString.decompress(compressedData));
        } catch (error) {
            console.error('Error decompressing orders data:', error);
            return [];
        }
    }, []);

    // Memoize all restock calculations to avoid repeated expensive operations
    const restockDataCache = useMemo(() => {
        if (!decompressedOrdersData.length) return {};

        const cache = {};
        const now = new Date();
        const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        const sixtyDaysAgo = new Date(now - 60 * 24 * 60 * 60 * 1000);

        console.log('Processing orders for restock data:', {
            totalOrders: decompressedOrdersData.length,
            thirtyDaysAgo: thirtyDaysAgo.toISOString(),
            sixtyDaysAgo: sixtyDaysAgo.toISOString()
        });

        // Pre-process all sales data
        const salesData = {};
        decompressedOrdersData.forEach(order => {
            const orderDate = new Date(order.created_at);
            if (order.line_items) {
                order.line_items.forEach(item => {
                    // Convert to string to ensure consistent key format
                    const key = `${String(item.product_id)}-${String(item.variant_id)}`;
                    if (!salesData[key]) {
                        salesData[key] = { sales30Days: 0, sales60Days: 0 };
                    }
                    
                    const quantity = parseInt(item.quantity) || 0;
                    if (orderDate >= thirtyDaysAgo) {
                        salesData[key].sales30Days += quantity;
                    }
                    if (orderDate >= sixtyDaysAgo) {
                        salesData[key].sales60Days += quantity;
                    }
                });
            }
        });

        console.log('Sales data processed:', Object.keys(salesData).length, 'unique variants');

        // Calculate restock data for all product-variant combinations
        processedData.forEach(product => {
            product.variants.forEach(variant => {
                // Convert to string to ensure consistent key format
                const key = `${String(product.product_id)}-${String(variant.id)}`;
                const currentStock = variant.inventory_quantity || 0;
                const sales = salesData[key] || { sales30Days: 0, sales60Days: 0 };

                // Calculate daily average and restock recommendations
                const dailyAvg30 = sales.sales30Days / 30;
                const dailyAvg60 = sales.sales60Days / 60;
                
                // Calculate weeks of stock remaining
                const weeksRemaining30 = dailyAvg30 > 0 ? (currentStock / dailyAvg30) / 7 : Infinity;
                const weeksRemaining60 = dailyAvg60 > 0 ? (currentStock / dailyAvg60) / 7 : Infinity;
                
                // Restock recommendations (assume 4-week lead time)
                const recommended30 = Math.max(0, Math.ceil(dailyAvg30 * 28) - currentStock);
                const recommended60 = Math.max(0, Math.ceil(dailyAvg60 * 28) - currentStock);
                
                cache[key] = {
                    sales30Days: sales.sales30Days,
                    sales60Days: sales.sales60Days,
                    dailyAvg30,
                    dailyAvg60,
                    weeksRemaining30,
                    weeksRemaining60,
                    recommended30,
                    recommended60
                };

                // Debug log for first few items
                if (Object.keys(cache).length <= 3) {
                    console.log('Sample restock data:', {
                        key,
                        currentStock,
                        sales30Days: sales.sales30Days,
                        sales60Days: sales.sales60Days,
                        dailyAvg30,
                        dailyAvg60,
                        recommendations: { recommended30, recommended60 }
                    });
                }
            });
        });

        console.log('Restock cache created with', Object.keys(cache).length, 'entries');
        return cache;
    }, [decompressedOrdersData, processedData]);

    // Simple function to get cached restock data
    const getRestockData = (productId, variantId) => {
        // Convert to string to ensure consistent key format
        const key = `${String(productId)}-${String(variantId)}`;
        const result = restockDataCache[key] || {
            sales30Days: 0,
            sales60Days: 0,
            dailyAvg30: 0,
            dailyAvg60: 0,
            weeksRemaining30: Infinity,
            weeksRemaining60: Infinity,
            recommended30: 0,
            recommended60: 0
        };
        
        // Debug log for first few lookups
        if (Math.random() < 0.01) { // Log ~1% of lookups
            console.log('Restock lookup:', { key, found: !!restockDataCache[key], result });
        }
        
        return result;
    };

    // Sorting functions
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = (data) => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            let aValue, bValue;

            if (sortConfig.key === 'totalStock') {
                // Calculate total stock for selected variants only
                const aSelectedVariants = selectedVariants.map(variantTitle => 
                    a.variants.find(v => v.title === variantTitle)
                ).filter(Boolean);
                const bSelectedVariants = selectedVariants.map(variantTitle => 
                    b.variants.find(v => v.title === variantTitle)
                ).filter(Boolean);
                
                aValue = aSelectedVariants.reduce((sum, v) => sum + (v.inventory_quantity || 0), 0);
                bValue = bSelectedVariants.reduce((sum, v) => sum + (v.inventory_quantity || 0), 0);
            } else if (sortConfig.key === 'totalSales') {
                // Calculate total sales for selected variants only
                const aSelectedVariants = selectedVariants.map(variantTitle => 
                    a.variants.find(v => v.title === variantTitle)
                ).filter(Boolean);
                const bSelectedVariants = selectedVariants.map(variantTitle => 
                    b.variants.find(v => v.title === variantTitle)
                ).filter(Boolean);
                
                aValue = aSelectedVariants.reduce((sum, variant) => {
                    const salesKey = `${String(a.product_id)}-${String(variant.id)}`;
                    return sum + (variantSales[salesKey] || 0);
                }, 0);
                bValue = bSelectedVariants.reduce((sum, variant) => {
                    const salesKey = `${String(b.product_id)}-${String(variant.id)}`;
                    return sum + (variantSales[salesKey] || 0);
                }, 0);
            } else if (sortConfig.key.startsWith('variant-')) {
                // Sort by specific variant stock or sales
                // Key format: variant-stock-36/S or variant-sales-36/S
                const keyParts = sortConfig.key.split('-');
                const field = keyParts[1]; // 'stock' or 'sales'
                const variantName = keyParts.slice(2).join('-'); // Join back in case variant name has dashes
                
                const aVariant = a.variants.find(v => v.title === variantName);
                const bVariant = b.variants.find(v => v.title === variantName);
                
                if (field === 'stock') {
                    aValue = aVariant ? (aVariant.inventory_quantity || 0) : 0;
                    bValue = bVariant ? (bVariant.inventory_quantity || 0) : 0;
                } else if (field === 'sales') {
                    const aSalesKey = aVariant ? `${String(a.product_id)}-${String(aVariant.id)}` : null;
                    const bSalesKey = bVariant ? `${String(b.product_id)}-${String(bVariant.id)}` : null;
                    aValue = aSalesKey ? (variantSales[aSalesKey] || 0) : 0;
                    bValue = bSalesKey ? (variantSales[bSalesKey] || 0) : 0;
                }
            } else if (sortConfig.key === 'productName') {
                aValue = a.product_title.toLowerCase();
                bValue = b.product_title.toLowerCase();
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Apply search and category filters
    useEffect(() => {
        let filtered = processedData;

        // Apply category filter
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(product => 
                Array.isArray(product.tags) && product.tags.includes(selectedCategory)
            );
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product => 
                product.product_title.toLowerCase().includes(term) ||
                product.variants.some(variant => variant.sku?.toLowerCase().includes(term))
            );
        }

        setFilteredInventory(filtered);
        setCurrentPage(1);
    }, [processedData, searchTerm, selectedCategory]);

    // Handle variant selection
    const handleVariantToggle = (variantTitle) => {
        setSelectedVariants(prev => {
            if (prev.includes(variantTitle)) {
                return prev.filter(v => v !== variantTitle);
            }
            return [...prev, variantTitle];
        });
    };

    // Reset selected variants when inventory changes
    useEffect(() => {
        setSelectedVariants(allVariants.slice(0, 4)); // Default to first 4 variants
    }, [allVariants]);

    // Fetch costs data when component mounts
    useEffect(() => {
        fetchCosts();
    }, []);

    const fetchCosts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/costs`);
            if (response.ok) {
                const data = await response.json();
                setProductCosts(data.costs || {});
            }
        } catch (error) {
            console.error("Error fetching costs:", error);
        }
    };

    const paginateData = (data) => {
        const indexOfLastRow = currentPage * rowsPerPage;
        const indexOfFirstRow = indexOfLastRow - rowsPerPage;
        return data.slice(indexOfFirstRow, indexOfLastRow);
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `${parseFloat(price).toFixed(2)} RON`;
    };

    const formatStockDuration = (weeks) => {
        if (weeks === 0) return 'Out';
        if (weeks === Infinity || weeks > 52) return '1Y+';
        if (weeks >= 26) return `${Math.round(weeks / 4)}M`; // Only show months for 6+ months
        return `${Math.round(weeks)}W`; // Show weeks for anything under 6 months
    };

    const calculatePricingData = (price, compareAtPrice, cost) => {
        const priceNum = parseFloat(price) || 0;
        const compareAtPriceNum = parseFloat(compareAtPrice) || 0;
        const costNum = parseFloat(cost) || 0;
        
        // Price excluding 19% VAT
        const priceExVat = priceNum / 1.19;
        const costExVat = costNum;
        
        // Gross profit calculations
        const grossProfitAbs = priceExVat - costExVat;
        const grossProfitPercent = costExVat > 0 ? ((grossProfitAbs / priceExVat) * 100) : 0;
        
        return {
            price: priceNum,
            compareAtPrice: compareAtPriceNum,
            cost: costNum,
            priceExVat,
            grossProfitAbs,
            grossProfitPercent
        };
    };

    const ProductInfo = ({ product }) => {
        const variant = product.variants[0];
        const isCore = coreProducts && coreProducts[product.product_id];
        
        return (
            <div className="product-info">
                <div className="product-title-row">
                    <span className="product-title">{product.product_title}</span>
                    {isCore ? (
                        <span className="core-pill core">CORE</span>
                    ) : (
                        <span className="core-pill non-core">NON-CORE</span>
                    )}
                </div>
                <div className="product-details">
                    <span className="product-sku">{variant.sku}</span>
                </div>
            </div>
        );
    };

    const PricingInfo = ({ product }) => {
        const variant = product.variants[0];
        const productCost = productCosts[product.product_id]?.cost || '';
        const hasDiscount = variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price);
        
        const pricingData = calculatePricingData(variant.price, variant.compare_at_price, productCost);
        
        return (
            <div className="pricing-info">
                <div className="price-section">
                    {hasDiscount && (
                        <div className="discount-price">
                            <span className="was-label">Was:</span>
                            <span className="was-value">{formatPrice(pricingData.compareAtPrice)}</span>
                        </div>
                    )}
                    <div className="price-line">
                        <span className="price-main">{formatPrice(pricingData.price)}</span>
                        <span className="price-ex-vat">({formatPrice(pricingData.priceExVat)} ex-VAT)</span>
                    </div>
                </div>
                
                {productCost && (
                    <div className="cost-margin-section">
                        <div className="cost-line">
                            <span className="cost-label">Cost:</span>
                            <span className="cost-value">{formatPrice(productCost)}</span>
                        </div>
                        <div className="profit-line">
                            <span className="profit-abs">{formatPrice(pricingData.grossProfitAbs)}</span>
                            <span className="profit-percent">({pricingData.grossProfitPercent.toFixed(1)}%)</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const Pagination = ({ totalItems }) => {
        const totalPages = Math.ceil(totalItems / rowsPerPage);
        
        return (
            <div className="pagination">
                <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-button"
                >
                    Previous
                </button>
                <span className="pagination-info">
                    Page {currentPage} of {totalPages} ({totalItems} variants)
                </span>
                <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                >
                    Next
                </button>
            </div>
        );
    };

    // Cross-Category Variant Analysis
    const crossCategoryAnalysis = useMemo(() => {
        const targetVariants = ["36/S", "38/M", "40-42/L", "44/XL"];
        const categoryAnalysis = {};

        // Initialize analysis structure
        allCategories.forEach(category => {
            categoryAnalysis[category] = {};
            targetVariants.forEach(variant => {
                categoryAnalysis[category][variant] = {
                    totalStock: 0,
                    totalSales30Days: 0,
                    stockWeeks: 0,
                    productsCount: 0,
                    outOfStockCount: 0,
                    products: []
                };
            });
        });

        // Process all inventory data (not just filtered)
        processedData.forEach(product => {
            // Get product categories
            const productCategories = Array.isArray(product.tags) 
                ? product.tags.filter(tag => tag.startsWith('stock-'))
                : [];

            // If product has no stock categories, skip
            if (productCategories.length === 0) return;

            product.variants.forEach(variant => {
                if (targetVariants.includes(variant.title)) {
                    const stock = variant.inventory_quantity || 0;
                    const restockData = getRestockData(product.product_id, variant.id);

                    productCategories.forEach(category => {
                        const categoryData = categoryAnalysis[category];
                        if (categoryData && categoryData[variant.title]) {
                            const variantData = categoryData[variant.title];
                            
                            variantData.totalStock += stock;
                            variantData.totalSales30Days += restockData.sales30Days || 0;
                            variantData.productsCount++;
                            
                            if (stock === 0) variantData.outOfStockCount++;
                            
                            variantData.products.push({
                                productTitle: product.product_title,
                                productId: product.product_id,
                                imageUrl: product.image_url,
                                stock,
                                sales30Days: restockData.sales30Days
                            });
                        }
                    });
                }
            });
        });

        // Calculate stock weeks for each category-variant combination
        Object.keys(categoryAnalysis).forEach(category => {
            targetVariants.forEach(variant => {
                const data = categoryAnalysis[category][variant];
                const dailyAvg = data.totalSales30Days / 30;
                data.stockWeeks = dailyAvg > 0 ? (data.totalStock / dailyAvg) / 7 : 
                    (data.totalStock > 0 ? Infinity : 0);
            });
        });

        return categoryAnalysis;
    }, [processedData, allCategories, getRestockData]);

    return (
        <div className="sales-analytics-container">
            <div className="analytics-header">
                <h2>
                    Sales Analytics - Product Variants
                    {selectedCategory !== 'all' && (
                        <span className="category-indicator">
                            ({selectedCategory.replace('stock-', '')})
                        </span>
                    )}
                </h2>
                <CrossCategoryVariantAnalysis 
                    crossCategoryAnalysis={crossCategoryAnalysis}
                    getStockHealthColor={(weeks) => {
                        if (weeks === 0) return '#dc3545'; // Red - out of stock
                        if (weeks < 2) return '#fd7e14'; // Orange - critical
                        if (weeks < 4) return '#ffc107'; // Yellow - low
                        if (weeks < 8) return '#28a745'; // Green - good
                        return '#17a2b8'; // Blue - overstocked
                    }}
                />
            </div>

            <div className="controls-container">
                <div className="search-and-filter-row">
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search by product name or SKU..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="category-filter-container">
                        <label htmlFor="category-select">Category:</label>
                        <select
                            id="category-select"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="category-select"
                        >
                            <option value="all">All Categories</option>
                            {allCategories.map(category => (
                                <option key={category} value={category}>
                                    {category.replace('stock-', '')}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="variant-selector">
                    <h4>Select Variants to Display:</h4>
                    <div className="variant-options">
                        {allVariants.map(variant => (
                            <label key={variant} className="variant-checkbox">
                                <input
                                    type="checkbox"
                                    checked={selectedVariants.includes(variant)}
                                    onChange={() => handleVariantToggle(variant)}
                                />
                                {variant}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <CategoryStockAnalysis 
                filteredInventory={filteredInventory}
                selectedCategory={selectedCategory}
                restockDataCache={restockDataCache}
                getRestockData={getRestockData}
            />

            <div className="analytics-table-container">
                <table className="analytics-table">
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th 
                                className="sortable"
                                onClick={() => handleSort('productName')}
                            >
                                Product Name
                                {sortConfig.key === 'productName' && (
                                    <span className="sort-indicator">
                                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                                    </span>
                                )}
                            </th>
                            <th>Pricing & Margin</th>
                            {selectedVariants.map(variant => (
                                <th key={variant}>
                                    <div className="variant-header">
                                        <div className="variant-title">{variant}</div>
                                        <div className="variant-sort-buttons">
                                            <button
                                                className={`sort-button ${sortConfig.key === `variant-stock-${variant}` ? 'active' : ''}`}
                                                onClick={() => handleSort(`variant-stock-${variant}`)}
                                                title="Sort by stock"
                                            >
                                                Stock
                                                {sortConfig.key === `variant-stock-${variant}` && (
                                                    <span className="sort-indicator">
                                                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                                                    </span>
                                                )}
                                            </button>
                                            <button
                                                className={`sort-button ${sortConfig.key === `variant-sales-${variant}` ? 'active' : ''}`}
                                                onClick={() => handleSort(`variant-sales-${variant}`)}
                                                title="Sort by sales"
                                            >
                                                Sales
                                                {sortConfig.key === `variant-sales-${variant}` && (
                                                    <span className="sort-indicator">
                                                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </th>
                            ))}
                            <th>
                                <div className="totals-header">
                                    <div className="totals-title">Totals</div>
                                    <div className="totals-sort-buttons">
                                        <button
                                            className={`sort-button ${sortConfig.key === 'totalStock' ? 'active' : ''}`}
                                            onClick={() => handleSort('totalStock')}
                                            title="Sort by total stock"
                                        >
                                            Stock
                                            {sortConfig.key === 'totalStock' && (
                                                <span className="sort-indicator">
                                                    {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            className={`sort-button ${sortConfig.key === 'totalSales' ? 'active' : ''}`}
                                            onClick={() => handleSort('totalSales')}
                                            title="Sort by total sales"
                                        >
                                            Sales
                                            {sortConfig.key === 'totalSales' && (
                                                <span className="sort-indicator">
                                                    {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginateData(getSortedData(filteredInventory)).map((product) => {
                            // Calculate totals only for selected variants to match displayed columns
                            const selectedVariantData = selectedVariants.map(variantTitle => 
                                product.variants.find(v => v.title === variantTitle)
                            ).filter(Boolean);
                            
                            const totalStock = selectedVariantData.reduce(
                                (sum, v) => sum + (v.inventory_quantity || 0), 
                                0
                            );
                            
                            const totalSales = selectedVariantData.reduce((sum, variant) => {
                                const salesKey = `${String(product.product_id)}-${String(variant.id)}`;
                                return sum + (variantSales[salesKey] || 0);
                            }, 0);

                            // Debug logging for first few products
                            if (Math.random() < 0.05) { // Log ~5% of products
                                console.log('Product totals check:', {
                                    productTitle: product.product_title,
                                    selectedVariants: selectedVariants,
                                    selectedVariantData: selectedVariantData.map(v => ({
                                        title: v.title,
                                        stock: v.inventory_quantity,
                                        id: v.id
                                    })),
                                    totalStock,
                                    totalSales
                                });
                            }
                            
                            return (
                                <tr key={product.product_id}>
                                    <td className="image-cell">
                                        {product.image_url ? (
                                            <img 
                                                src={product.image_url} 
                                                alt={product.product_title}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <div className="no-image">No Image</div>
                                        )}
                                    </td>
                                    <td>
                                        <ProductInfo product={product} />
                                    </td>
                                    <td className="pricing-column">
                                        <PricingInfo product={product} />
                                    </td>
                                    {selectedVariants.map(variantTitle => {
                                        const variant = product.variants.find(v => v.title === variantTitle);
                                        const salesKey = variant ? `${String(product.product_id)}-${String(variant.id)}` : null;
                                        const salesCount = salesKey ? variantSales[salesKey] || 0 : 0;
                                        const stock = variant ? variant.inventory_quantity || 0 : 0;
                                        
                                        const restockData = variant ? getRestockData(product.product_id, variant.id) : null;
                                        const stockDuration = restockData ? restockData.weeksRemaining60 : Infinity;
                                        
                                        return (
                                            <td key={variantTitle} className="variant-analytics-cell">
                                                {variant ? (
                                                    <div className="variant-data">
                                                        <div className="stock-sales-row">
                                                            <span className={`stock-value ${stock === 0 ? 'out-of-stock' : stock < 5 ? 'low-stock' : ''}`}>
                                                                {stock}
                                                            </span>
                                                            <span className="divider">/</span>
                                                            <span className="sales-value">
                                                                {salesCount}
                                                            </span>
                                                        </div>
                                                        <div className="turnover-row">
                                                            <span className="turnover-label">Est. Duration:</span>
                                                            <span className={`turnover-value ${stockDuration < 26 && stockDuration > 0 && stockDuration !== Infinity ? 'duration-weeks' : ''}`}>
                                                                {formatStockDuration(stockDuration)}
                                                            </span>
                                                        </div>
                                                                                                {restockData && (
                                            <div className="restock-row">
                                                <span className="restock-label">30d:</span>
                                                <span className="restock-value">{restockData.sales30Days}</span>
                                                <span className="restock-divider">|</span>
                                                <span className="restock-label">60d:</span>
                                                <span className="restock-value">{restockData.sales60Days}</span>
                                            </div>
                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="no-variant">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="combined-totals-cell">
                                        <div className="totals-data">
                                            <div className="totals-row">
                                                <span className="total-stock-value">{totalStock}</span>
                                                <span className="divider">/</span>
                                                <span className="total-sales-value">{totalSales}</span>
                                            </div>
                                            <div className="totals-labels">
                                                <span className="stock-label">Stock</span>
                                                <span className="sales-label">Sales</span>
                                            </div>
                                            {(() => {
                                                let totalRestock30 = 0;
                                                let totalRestock60 = 0;
                                                // Calculate totals only for selected variants to match displayed columns
                                                selectedVariantData.forEach(variant => {
                                                    const restockData = getRestockData(product.product_id, variant.id);
                                                    totalRestock30 += restockData.sales30Days || 0;
                                                    totalRestock60 += restockData.sales60Days || 0;
                                                });
                                                return (
                                                    <div className="totals-restock">
                                                        <span className="restock-label">30d:</span>
                                                        <span className="restock-value">{totalRestock30}</span>
                                                        <span className="restock-divider">|</span>
                                                        <span className="restock-label">60d:</span>
                                                        <span className="restock-value">{totalRestock60}</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <Pagination totalItems={getSortedData(filteredInventory).length} />
            </div>
        </div>
    );
};

// Cross-Category Variant Analysis Component
const CrossCategoryVariantAnalysis = ({ crossCategoryAnalysis, getStockHealthColor }) => {
    const targetVariants = ["36/S", "38/M", "40-42/L", "44/XL"];
    
    // Calculate variant-level insights across all categories
    const variantInsights = useMemo(() => {
        const insights = {};
        
        targetVariants.forEach(variant => {
            let totalStock = 0;
            let totalSales = 0;
            let criticalCategories = [];
            let overstockedCategories = [];
            let wellStockedCategories = [];
            
            Object.entries(crossCategoryAnalysis).forEach(([category, data]) => {
                const variantData = data[variant];
                if (variantData) {
                    totalStock += variantData.totalStock;
                    totalSales += variantData.totalSales30Days;
                    
                    if (variantData.stockWeeks === 0 || (variantData.stockWeeks < 2 && variantData.stockWeeks !== Infinity)) {
                        criticalCategories.push({
                            category: category.replace('stock-', ''),
                            stockWeeks: variantData.stockWeeks,
                            stock: variantData.totalStock,
                            sales: variantData.totalSales30Days
                        });
                    } else if (variantData.stockWeeks > 8 && variantData.stockWeeks !== Infinity) {
                        overstockedCategories.push({
                            category: category.replace('stock-', ''),
                            stockWeeks: variantData.stockWeeks,
                            stock: variantData.totalStock,
                            sales: variantData.totalSales30Days
                        });
                    } else if (variantData.stockWeeks >= 2 && variantData.stockWeeks <= 8) {
                        wellStockedCategories.push({
                            category: category.replace('stock-', ''),
                            stockWeeks: variantData.stockWeeks,
                            stock: variantData.totalStock,
                            sales: variantData.totalSales30Days
                        });
                    }
                }
            });
            
            // Use the lowest stock duration across all categories for this variant (most critical)
            let overallStockWeeks = Infinity;
            Object.entries(crossCategoryAnalysis).forEach(([category, data]) => {
                const variantData = data[variant];
                if (variantData && variantData.stockWeeks < overallStockWeeks) {
                    overallStockWeeks = variantData.stockWeeks;
                }
            });
            
            // If all categories have infinity (no sales), but have stock, set to infinity
            // If no stock at all, set to 0
            if (overallStockWeeks === Infinity) {
                overallStockWeeks = totalStock > 0 ? Infinity : 0;
            }
            
            insights[variant] = {
                totalStock,
                totalSales,
                overallStockWeeks,
                criticalCategories: criticalCategories.sort((a, b) => a.stockWeeks - b.stockWeeks),
                overstockedCategories: overstockedCategories.sort((a, b) => b.stockWeeks - a.stockWeeks),
                wellStockedCategories,
                needsUrgentBuying: criticalCategories.length > 0,
                hasReplacementStock: overstockedCategories.length > 0
            };
        });
        
        return insights;
    }, [crossCategoryAnalysis]);

    return (
        <div className="cross-category-analysis">
            <h3>🔄 Cross-Category Variant Analysis - Stock & Sales Overview</h3>
            
            <div className="category-bar-chart-container">
                <div className="chart-header">
                    <div className="chart-title">Stock Duration by Category & Variant</div>
                </div>
                
                <div className="category-bars">
                    {(() => {
                        // Use a fixed scale with 100 weeks as full width
                        // Anything above 100 weeks (including Infinity) shows as full width
                        const maxScaleWeeks = 50;
                        
                        return Object.entries(crossCategoryAnalysis).map(([category, variantData]) => {
                            const categoryName = category.replace('stock-', '');
                            
                            return (
                                <div key={category} className="category-bar-row">
                                    <div className="category-label">
                                        <span className="category-name-text">{categoryName}</span>
                                    </div>
                                    <div className="variant-bars-container">
                                        {targetVariants.map(variant => {
                                            const data = variantData[variant];
                                            const stockWeeks = data.stockWeeks;
                                            const healthColor = getStockHealthColor(stockWeeks);
                                            
                                            // Calculate bar width as percentage of available space
                                            // Scale relative to 100 weeks max (anything above = full width)
                                            let barWidthPercent;
                                            if (stockWeeks === 0) {
                                                barWidthPercent = 3; // Small for out-of-stock (0 weeks)
                                            } else if (stockWeeks === Infinity || stockWeeks >= maxScaleWeeks) {
                                                barWidthPercent = 100; // Full width for infinity or 100+ weeks
                                            } else {
                                                barWidthPercent = Math.max(5, (stockWeeks / maxScaleWeeks) * 100);
                                            }
                                            
                                            const durationText = stockWeeks === Infinity ? '∞' : 
                                                               stockWeeks === 0 ? '0w' :
                                                               `${Math.round(stockWeeks * 10) / 10}w`;
                                            
                                            return (
                                                <div key={variant} className="variant-bar-wrapper">
                                                    <div className="variant-bar-container">
                                                        <div className="variant-info-inline">
                                                            <span className="variant-label">{variant}</span>
                                                            <span className="stock-info">{data.totalStock}</span>
                                                            <span className="divider">|</span>
                                                            <span className="sales-info">{data.totalSales30Days}</span>
                                                        </div>
                                                        <div className="variant-bar-wrapper-inner">
                                                            <div 
                                                                className="variant-bar"
                                                                style={{ 
                                                                    width: `${barWidthPercent}%`,
                                                                    backgroundColor: healthColor
                                                                }}
                                                                title={`${variant}: ${data.totalStock} stock | ${data.totalSales30Days} sales | ${durationText}`}
                                                            >
                                                                <div className="bar-duration-label">
                                                                    {durationText}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>
            
            {/* Legend */}
            <div className="chart-legend">
                <div className="legend-section">
                    <span className="legend-title">Bar Height = Stock Duration (weeks)</span>
                    <span className="legend-subtitle">Numbers below bars: Stock | Sales</span>
                </div>
                <div className="legend-colors">
                    <span className="legend-color" style={{ backgroundColor: '#dc3545' }}>Critical (0-4w)</span>
                    <span className="legend-color" style={{ backgroundColor: '#fd7e14' }}>Urgent (4-6w)</span>
                    <span className="legend-color" style={{ backgroundColor: '#ffc107' }}>Low (6-10w)</span>
                    <span className="legend-color" style={{ backgroundColor: '#28a745' }}>Good (10-16w)</span>
                    <span className="legend-color" style={{ backgroundColor: '#17a2b8' }}>Overstocked (16w+)</span>
                </div>
            </div>
        </div>
    );
};

// Category Stock Analysis Component
const CategoryStockAnalysis = ({ filteredInventory, selectedCategory, getRestockData }) => {
    const categoryAnalysis = useMemo(() => {
        if (!filteredInventory.length) return null;

        // Define the target variants
        const targetVariants = ["36/S", "38/M", "40-42/L", "44/XL"];
        
        let totalCurrentStock = 0;
        let totalSales30Days = 0;
        let totalSales60Days = 0;
        let productsWithStock = 0;
        let productsOutOfStock = 0;
        let productsWithSales = 0;
        let worstStockWeeks = Infinity;
        let bestStockWeeks = 0;
        const productDetails = [];
        
        // Variant-specific analysis
        const variantAnalysis = {};
        targetVariants.forEach(variantTitle => {
            variantAnalysis[variantTitle] = {
                totalStock: 0,
                totalSales30Days: 0,
                avgDailySales: 0,
                stockWeeks: 0,
                productsCount: 0,
                outOfStockCount: 0,
                lowStockCount: 0,
                products: []
            };
        });

        filteredInventory.forEach(product => {
            product.variants.forEach(variant => {
                const stock = variant.inventory_quantity || 0;
                const restockData = getRestockData(product.product_id, variant.id);
                
                totalCurrentStock += stock;
                totalSales30Days += restockData.sales30Days || 0;
                totalSales60Days += restockData.sales60Days || 0;

                if (stock > 0) productsWithStock++;
                else productsOutOfStock++;
                
                if (restockData.sales30Days > 0) productsWithSales++;

                // Calculate individual stock duration
                const dailyAvg = (restockData.sales30Days || 0) / 30;
                const stockWeeks = dailyAvg > 0 ? (stock / dailyAvg) / 7 : (stock > 0 ? Infinity : 0);
                
                if (stockWeeks > 0 && stockWeeks < worstStockWeeks) {
                    worstStockWeeks = stockWeeks;
                }
                if (stockWeeks > bestStockWeeks) {
                    bestStockWeeks = stockWeeks;
                }

                productDetails.push({
                    productTitle: product.product_title,
                    variantTitle: variant.title,
                    stock,
                    sales30Days: restockData.sales30Days,
                    stockWeeks,
                    isOutOfStock: stock === 0,
                    hasSales: restockData.sales30Days > 0
                });

                // Aggregate by variant size if it's one of our target variants
                if (targetVariants.includes(variant.title)) {
                    const variantData = variantAnalysis[variant.title];
                    variantData.totalStock += stock;
                    variantData.totalSales30Days += restockData.sales30Days || 0;
                    variantData.productsCount++;
                    
                    if (stock === 0) variantData.outOfStockCount++;
                    if (stock > 0 && stock < 10) variantData.lowStockCount++;
                    
                    variantData.products.push({
                        productTitle: product.product_title,
                        productId: product.product_id,
                        imageUrl: product.image_url,
                        stock,
                        sales30Days: restockData.sales30Days,
                        stockWeeks
                    });
                }
            });
        });

        // Calculate variant-specific metrics
        Object.keys(variantAnalysis).forEach(variantTitle => {
            const variantData = variantAnalysis[variantTitle];
            variantData.avgDailySales = variantData.totalSales30Days / 30;
            variantData.stockWeeks = variantData.avgDailySales > 0 
                ? (variantData.totalStock / variantData.avgDailySales) / 7 
                : (variantData.totalStock > 0 ? Infinity : 0);
        });

        // Calculate category-level metrics
        const avgDailySales30 = totalSales30Days / 30;
        const avgDailySales60 = totalSales60Days / 60;
        
        // Use the lowest (most critical) variant duration as the category duration
        let categoryStockWeeks = Infinity;
        Object.values(variantAnalysis).forEach(variantData => {
            if (variantData.stockWeeks < categoryStockWeeks) {
                categoryStockWeeks = variantData.stockWeeks;
            }
        });
        
        // If all variants have infinite duration but have stock, keep infinity
        // If no stock at all, set to 0
        if (categoryStockWeeks === Infinity) {
            categoryStockWeeks = totalCurrentStock > 0 ? Infinity : 0;
        }
        
        // Identify critical situations
        const outOfStockWithSales = productDetails.filter(p => p.isOutOfStock && p.hasSales);
        const lowStockWithSales = productDetails.filter(p => p.stock > 0 && p.stock < 10 && p.hasSales);
        const overstocked = productDetails.filter(p => p.stockWeeks > 12 && p.stock > 0);

        return {
            totalCurrentStock,
            totalSales30Days,
            avgDailySales30,
            avgDailySales60,
            categoryStockWeeks,
            productsWithStock,
            productsOutOfStock,
            productsWithSales,
            worstStockWeeks: worstStockWeeks === Infinity ? 0 : worstStockWeeks,
            bestStockWeeks: bestStockWeeks === Infinity ? 0 : bestStockWeeks,
            outOfStockWithSales,
            lowStockWithSales,
            overstocked,
            productDetails,
            variantAnalysis
        };
    }, [filteredInventory, getRestockData]);

    if (!categoryAnalysis) return null;

    const getStockHealthColor = (weeks) => {
        if (weeks === 0) return '#dc3545'; // Red - out of stock
        if (weeks < 4) return '#dc3545'; // Red - critical (less than lead time)
        if (weeks < 6) return '#fd7e14'; // Orange - urgent (lead time + small buffer)
        if (weeks < 10) return '#ffc107'; // Yellow - low (decent buffer)
        if (weeks < 16) return '#28a745'; // Green - good
        return '#17a2b8'; // Blue - overstocked
    };

    const getStockHealthLabel = (weeks) => {
        if (weeks === 0) return 'Out of Stock';
        if (weeks < 4) return 'Critical (< 4 weeks - Below Lead Time)';
        if (weeks < 6) return 'Urgent (4-6 weeks - Minimal Buffer)';
        if (weeks < 10) return 'Low (6-10 weeks - Small Buffer)';
        if (weeks < 16) return 'Good (10-16 weeks)';
        return 'Overstocked (> 16 weeks)';
    };

    const chartData = {
        labels: ['Current Stock Duration'],
        datasets: [{
            label: 'Weeks of Stock',
            data: [Math.round(categoryAnalysis.categoryStockWeeks * 10) / 10],
            backgroundColor: getStockHealthColor(categoryAnalysis.categoryStockWeeks),
            borderColor: getStockHealthColor(categoryAnalysis.categoryStockWeeks),
            borderWidth: 2
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                max: 16,
                title: {
                    display: true,
                    text: 'Weeks'
                },
                ticks: {
                    callback: function(value) {
                        return value + 'w';
                    }
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Category Analysis'
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `${context.parsed.y} weeks of stock remaining`;
                    }
                }
            }
        }
    };

    return (
        <div className="category-stock-analysis">
            <h3>
                Category Stock Duration Analysis
                {selectedCategory !== 'all' && (
                    <span className="category-name"> - {selectedCategory.replace('stock-', '')}</span>
                )}
            </h3>
            
            <div className="analysis-grid">
                <div className="stock-overview-card">
                    <h4>Stock Overview</h4>
                    <div className="overview-metrics">
                        <div className="metric">
                            <span className="metric-label">Current Stock:</span>
                            <span className="metric-value">{categoryAnalysis.totalCurrentStock} units</span>
                        </div>
                        <div className="metric">
                            <span className="metric-label">30-day Sales:</span>
                            <span className="metric-value">{categoryAnalysis.totalSales30Days} units</span>
                        </div>
                        <div className="metric">
                            <span className="metric-label">Daily Average:</span>
                            <span className="metric-value">{Math.round(categoryAnalysis.avgDailySales30 * 10) / 10} units/day</span>
                        </div>
                        <div className="metric main-metric">
                            <span className="metric-label">Category Duration:</span>
                            <span 
                                className="metric-value duration-value" 
                                style={{ color: getStockHealthColor(categoryAnalysis.categoryStockWeeks) }}
                            >
                                {categoryAnalysis.categoryStockWeeks === Infinity 
                                    ? 'No Sales Data' 
                                    : `${Math.round(categoryAnalysis.categoryStockWeeks * 10) / 10} weeks`
                                }
                            </span>
                        </div>
                    </div>
                    <div className="stock-status">
                        <span 
                            className="status-badge"
                            style={{ 
                                backgroundColor: getStockHealthColor(categoryAnalysis.categoryStockWeeks),
                                color: 'white'
                            }}
                        >
                            {getStockHealthLabel(categoryAnalysis.categoryStockWeeks)}
                        </span>
                    </div>
                </div>

                <div className="visual-chart">
                    <div style={{ height: '200px' }}>
                        {/* We'll add a simple visual indicator instead of a complex chart for now */}
                        <div className="stock-gauge">
                            <div className="gauge-container">
                                <div className="gauge-header">Stock Duration</div>
                                <div className="gauge-value" style={{ color: getStockHealthColor(categoryAnalysis.categoryStockWeeks) }}>
                                    {categoryAnalysis.categoryStockWeeks === Infinity 
                                        ? '∞' 
                                        : `${Math.round(categoryAnalysis.categoryStockWeeks * 10) / 10}w`
                                    }
                                </div>
                                <div className="gauge-bar">
                                    <div 
                                        className="gauge-fill"
                                        style={{ 
                                            width: `${Math.min((categoryAnalysis.categoryStockWeeks / 12) * 100, 100)}%`,
                                            backgroundColor: getStockHealthColor(categoryAnalysis.categoryStockWeeks)
                                        }}
                                    ></div>
                                </div>
                                <div className="gauge-labels">
                                    <span>0w</span>
                                    <span>6w</span>
                                    <span>12w+</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="alerts-card">
                    <h4>Purchasing Alerts</h4>
                    <div className="alerts-list">
                        {categoryAnalysis.outOfStockWithSales.length > 0 && (
                            <div className="alert critical">
                                <strong>Critical:</strong> {categoryAnalysis.outOfStockWithSales.length} products out of stock but selling
                            </div>
                        )}
                        {categoryAnalysis.lowStockWithSales.length > 0 && (
                            <div className="alert warning">
                                <strong>Warning:</strong> {categoryAnalysis.lowStockWithSales.length} products with low stock
                            </div>
                        )}
                        {categoryAnalysis.overstocked.length > 0 && (
                            <div className="alert info">
                                <strong>Info:</strong> {categoryAnalysis.overstocked.length} products overstocked (save capital)
                            </div>
                        )}
                        {categoryAnalysis.categoryStockWeeks < 4 && categoryAnalysis.categoryStockWeeks > 0 && (
                            <div className="alert warning">
                                <strong>Recommendation:</strong> Consider restocking this category soon
                            </div>
                        )}
                        {categoryAnalysis.categoryStockWeeks > 8 && (
                            <div className="alert info">
                                <strong>Recommendation:</strong> Category well-stocked, focus budget elsewhere
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Variant Size Analysis */}
            <div className="variant-analysis">
                <h4>👕 Variant Size Analysis</h4>
                <div className="variant-grid">
                    {Object.entries(categoryAnalysis.variantAnalysis).map(([variantTitle, data]) => {
                        const variantStockWeeks = data.stockWeeks === Infinity ? '∞' : 
                            data.stockWeeks > 0 ? `${Math.round(data.stockWeeks * 10) / 10}w` : '0w';
                        
                        const variantGaugeColor = getStockHealthColor(data.stockWeeks);
                        const variantGaugeWidth = data.stockWeeks === 0 ? 5 :
                            data.stockWeeks === Infinity ? 100 :
                            Math.min((data.stockWeeks / 12) * 100, 100);

                        return (
                            <div key={variantTitle} className="variant-card">
                                <div className="variant-header">
                                    <span className="variant-title">{variantTitle}</span>
                                    <span 
                                        className="variant-duration"
                                        style={{ color: variantGaugeColor }}
                                    >
                                        {variantStockWeeks}
                                    </span>
                                </div>
                                
                                <div className="variant-metrics">
                                    <div className="variant-metric">
                                        <span className="variant-metric-label">Stock:</span>
                                        <span className="variant-metric-value">{data.totalStock}</span>
                                    </div>
                                    <div className="variant-metric">
                                        <span className="variant-metric-label">30d Sales:</span>
                                        <span className="variant-metric-value">{data.totalSales30Days}</span>
                                    </div>
                                    <div className="variant-metric">
                                        <span className="variant-metric-label">Products:</span>
                                        <span className="variant-metric-value">{data.productsCount}</span>
                                    </div>
                                </div>

                                <div className="variant-gauge">
                                    <div className="variant-gauge-bar">
                                        <div 
                                            className="variant-gauge-fill"
                                            style={{ 
                                                width: `${variantGaugeWidth}%`,
                                                backgroundColor: variantGaugeColor
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Product Images with Stock-Proportional Sizing */}
                                <div className="variant-product-images">
                                    {data.products
                                        .filter(product => product.stock > 0 && product.imageUrl) // Only show products with stock and images
                                        .sort((a, b) => b.stock - a.stock) // Sort by stock descending
                                        .slice(0, 12) // Show max 6 products to avoid overcrowding
                                        .map((product, index) => {
                                            // Calculate image size based on stock
                                            // Max size: 60px, Min size: 25px
                                            const maxStock = Math.max(...data.products.map(p => p.stock));
                                            const stockRatio = maxStock > 0 ? product.stock / maxStock : 0;
                                            const imageSize = Math.max(25, Math.min(60, 25 + (stockRatio * 35)));
                                            
                                            return (
                                                <div 
                                                    key={`${product.productId}-${index}`}
                                                    className="variant-product-item"
                                                    title={`${product.productTitle} - Stock: ${product.stock}`}
                                                >
                                                    <img 
                                                        src={product.imageUrl}
                                                        alt={product.productTitle}
                                                        style={{
                                                            width: `${imageSize}px`,
                                                            height: `${imageSize}px`,
                                                            objectFit: 'cover',
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    />
                                                    <div className="variant-product-stock">
                                                        {product.stock}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                    {data.products.filter(p => p.stock > 0 && p.imageUrl).length === 0 && (
                                        <div className="no-stock-images">
                                            No products with stock & images
                                        </div>
                                    )}
                                </div>

                                {/* Variant specific alerts */}
                                <div className="variant-alerts">
                                    {data.outOfStockCount > 0 && (
                                        <div className="variant-alert variant-alert-critical">
                                            🚨 {data.outOfStockCount} out of stock
                                        </div>
                                    )}
                                    {data.lowStockCount > 0 && (
                                        <div className="variant-alert variant-alert-warning">
                                            ⚠️ {data.lowStockCount} low stock
                                        </div>
                                    )}
                                    {data.stockWeeks > 0 && data.stockWeeks < 2 && (
                                        <div className="variant-alert variant-alert-warning">
                                            📉 Need urgent restock
                                        </div>
                                    )}
                                    {data.stockWeeks > 8 && (
                                        <div className="variant-alert variant-alert-info">
                                            📈 Well stocked
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SalesAnalytics; 